/**
 * TabBar: the RMS's places along the foot of a phone, where a thumb
 * already is (DESIGN-SYSTEM.md, Admin global navigation). Below `md` it
 * stands in for the sidebar: the places, the one search and Settings, an
 * icon over a word each. The account is in Settings → Profile, as a
 * phone app keeps it.
 *
 * The open place is said by its ink: with no sidebar and the title
 * scrolled away, the bar is the one thing left saying where you are.
 */
import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { isSettings } from "../../lib/last-place";
import { DESTINATIONS, isActiveDestination } from "./destinations";
import { GearIcon, SearchIcon } from "./icons";
import { SearchDialog } from "./SearchDialog";
import styles from "./TabBar.module.css";

function Tab({
  icon,
  label,
  on,
}: {
  icon: ReactNode;
  label: string;
  on: boolean;
}) {
  return (
    <>
      <span className={styles.icon}>{icon}</span>
      <span className={on ? styles.labelOn : styles.label}>{label}</span>
    </>
  );
}

export function TabBar() {
  const { pathname } = useLocation();
  const [searching, setSearching] = useState(false);
  const settings = isSettings(pathname);

  return (
    <nav aria-label="Tabs" className={styles.bar}>
      {DESTINATIONS.map((d) => {
        const on = isActiveDestination(pathname, d.to);
        return (
          <Link
            key={d.to}
            to={d.to}
            className={on ? styles.tabOn : styles.tab}
            aria-current={on ? "page" : undefined}
          >
            <Tab icon={d.icon} label={d.label} on={on} />
          </Link>
        );
      })}
      <button
        type="button"
        className={styles.tab}
        onClick={() => setSearching(true)}
      >
        <Tab icon={<SearchIcon />} label="Search" on={false} />
      </button>
      <Link
        to="/settings"
        className={settings ? styles.tabOn : styles.tab}
        aria-current={settings ? "page" : undefined}
      >
        <Tab icon={<GearIcon />} label="Settings" on={settings} />
      </Link>
      <SearchDialog open={searching} onClose={() => setSearching(false)} />
    </nav>
  );
}
